/**
 * UserRoleBadge Component
 * Badge pour afficher le rôle d'un utilisateur
 * Compatible avec les slugs dynamiques (ex: 'super-admin', 'observateur')
 */

import { Shield, ShieldCheck, Building2, MapPin, Settings, BarChart3, Eye, User } from 'lucide-react';

export type { UserRole } from '../../application/dto/dtos';

interface UserRoleBadgeProps {
  /** Slug du rôle (ex: 'super-admin') ou ancien enum (ex: 'SUPER_ADMIN') */
  role: string;
  /** Nom lisible du rôle (si fourni, prioritaire sur la dérivation depuis le slug) */
  roleNom?: string;
  size?: 'sm' | 'md' | 'lg';
}

type RoleConfig = {
  label: string;
  icon: typeof Shield;
  bgColor: string;
  textColor: string;
  borderColor: string;
};

/** Correspondance slug/enum → config d'affichage */
const roleConfigMap: Record<string, RoleConfig> = {
  // Slugs dynamiques (nouveau système)
  'super-admin': {
    label: 'Super Admin',
    icon: ShieldCheck,
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
  },
  'admin-organisation': {
    label: 'Admin Organisation',
    icon: Shield,
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200',
  },
  'admin-national': {
    label: 'Admin National',
    icon: Building2,
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-700',
    borderColor: 'border-indigo-200',
  },
  'admin-diocesain': {
    label: 'Admin Diocésain',
    icon: MapPin,
    bgColor: 'bg-violet-100',
    textColor: 'text-violet-700',
    borderColor: 'border-violet-200',
  },
  'gestionnaire': {
    label: 'Gestionnaire',
    icon: Settings,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
  },
  'analyste': {
    label: 'Analyste',
    icon: BarChart3,
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
  },
  'observateur': {
    label: 'Observateur',
    icon: Eye,
    bgColor: 'bg-surface-overlay',
    textColor: 'text-content-muted',
    borderColor: 'border-border',
  },
  // Anciens enums (rétro-compatibilité)
  'SUPER_ADMIN': {
    label: 'Super Admin',
    icon: ShieldCheck,
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
  },
  'ADMIN_CARITAS': {
    label: 'Admin Caritas',
    icon: Shield,
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200',
  },
  'ADMIN_NATIONAL': {
    label: 'Admin National',
    icon: Building2,
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-700',
    borderColor: 'border-indigo-200',
  },
  'ADMIN_DIOCESAIN': {
    label: 'Admin Diocésain',
    icon: MapPin,
    bgColor: 'bg-violet-100',
    textColor: 'text-violet-700',
    borderColor: 'border-violet-200',
  },
  'GESTIONNAIRE': {
    label: 'Gestionnaire',
    icon: Settings,
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
  },
  'ANALYSTE': {
    label: 'Analyste',
    icon: BarChart3,
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    borderColor: 'border-green-200',
  },
  'OBSERVATEUR': {
    label: 'Observateur',
    icon: Eye,
    bgColor: 'bg-surface-overlay',
    textColor: 'text-content-muted',
    borderColor: 'border-border',
  },
};

const defaultRoleConfig: RoleConfig = {
  label: 'Inconnu',
  icon: User,
  bgColor: 'bg-surface-overlay',
  textColor: 'text-content-muted',
  borderColor: 'border-border',
};

export function UserRoleBadge({ role, roleNom, size = 'md' }: UserRoleBadgeProps) {
  const roleConfig = roleConfigMap[role] ?? defaultRoleConfig;
  const Icon = roleConfig.icon;
  // Priorité : roleNom fourni > label dérivé de la config > role brut
  const label = roleNom ?? roleConfig.label;

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium
                ${roleConfig.bgColor} ${roleConfig.textColor} ${roleConfig.borderColor}
                ${sizeClasses[size]}`}
    >
      <Icon className={iconSizes[size]} />
      {label}
    </span>
  );
}
